import { useEffect, useState } from "react";
import { FaStar } from "react-icons/fa";
import { API_URL } from "../../../config/api";

const STORAGE_KEY = "neko_cli_rating_submitted_at";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export default function RatingWidget({ className = "" }) {
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [alreadyVoted, setAlreadyVoted] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/rating`)
      .then((res) => res.json())
      .then(setSummary)
      .catch(() => {});

    const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (last && Date.now() - last < ONE_DAY_MS) {
      setAlreadyVoted(true);
    }
  }, []);

  async function submitRating(value) {
    if (alreadyVoted || status === "submitting") return;
    setSelected(value);
    setStatus("submitting");
    try {
      const res = await fetch(`${API_URL}/api/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating: value }),
      });
      if (!res.ok) throw new Error("request failed");

      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      setAlreadyVoted(true);
      setStatus("done");

      const updated = await fetch(`${API_URL}/api/rating`).then((r) => r.json());
      setSummary(updated);
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={alreadyVoted}
            aria-label={`Rate ${star} stars`}
            className={`transition-transform ${alreadyVoted ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
            onMouseEnter={() => !alreadyVoted && setHovered(star)}
            onMouseLeave={() => !alreadyVoted && setHovered(0)}
            onClick={() => submitRating(star)}
          >
            <FaStar
              size={22}
              className={
                (hovered || selected || Math.round(summary.average)) >= star
                  ? "text-yellow-400"
                  : "text-pride-blue"
              }
            />
          </button>
        ))}
      </div>

      <p className="text-sm text-primary-400">
        {summary.count > 0 ? (
          <>
            {summary.average.toFixed(1)} / 5 · {summary.count} {summary.count === 1 ? "vote" : "votes"}
          </>
        ) : (
          "No votes yet"
        )}
      </p>

      {status === "done" && (
        <p className="text-xs text-green-400">Thanks for your vote! 🐾</p>
      )}
      {status === "error" && (
        <p className="text-xs text-red-400">Vote not recorded, please try again later.</p>
      )}
      {alreadyVoted && status !== "done" && (
        <p className="text-xs text-primary-500">You already voted today, come back tomorrow!</p>
      )}
    </div>
  );
}
